import SubscriberUtils from '../utils/subscriber-utils';
import WateringAdviceSubscriber from '../../types/WateringAdviceSubscriber';
import {getAppConfigVar} from '../config';
import {
	ApiSubscriberPreferencesSaveFieldError,
	ApiSubscriberPreferencesSaveResponse
} from '../../types/ApiSubscriberPreferencesSaveResponse';


/**
 *
 */
const setupSubscriberPreferencesForm = () =>
{
	const container = document.querySelector( '#subscriber-preferences-form-container' );

	if (!(container instanceof HTMLElement)) {
		return;
	}

	SubscriberUtils.getSubscriberInfo().then( subscriber =>
	{
		populateUserForm( subscriber );
	} ).catch( () =>
	{
		showErrorMessages( ['Error: Invalid link'] );
	} );
};


/**
 *
 * @param subscriber
 */
const populateUserForm = ( subscriber: WateringAdviceSubscriber ) =>
{
	const form               = document.querySelector( '#subscriber-preferences-form' );
	const notificationType   = document.querySelector( '#notification-type' );
	const subscriberInfo     = document.querySelector( '#subscriber-info' );
	const dateRangeContainer = document.querySelector( '#notify-date-range-container' );
	const subscriberToken    = document.querySelector( '#subscriber-token' );
	const startDayElem       = document.querySelector( '#notify-restrict-date-range-start-day' );
	const startMonthElem     = document.querySelector( '#notify-restrict-date-range-start-month' );
	const endDayElem         = document.querySelector( '#notify-restrict-date-range-end-day' );
	const endMonthElem       = document.querySelector( '#notify-restrict-date-range-end-month' );
	const monthSelects       = document.querySelectorAll( 'select[data-month-select]' );
	const token              = SubscriberUtils.getQueryParamToken();

	if (
		!(form instanceof HTMLFormElement)
		|| !(notificationType instanceof HTMLSelectElement)
		|| !(subscriberInfo instanceof HTMLElement)
		|| !(dateRangeContainer instanceof HTMLElement)
		|| !(subscriberToken instanceof HTMLInputElement)
		|| !(startDayElem instanceof HTMLSelectElement)
		|| !(startMonthElem instanceof HTMLSelectElement)
		|| !(endDayElem instanceof HTMLSelectElement)
		|| !(endMonthElem instanceof HTMLSelectElement)
	) {
		return;
	}


	const toggleNotificationType = () =>
	{
		if (notificationType.value === 'restricted-by-date-range') {
			dateRangeContainer.style.display = 'block';
		} else {
			dateRangeContainer.style.display = 'none';
		}
	};

	const updateDaysInMonths = () =>
	{
		monthSelects.forEach( select =>
		{
			if (select instanceof HTMLSelectElement) {

				const handle         = select.getAttribute( 'data-month-select' );
				const daySelect      = document.querySelector( 'select[data-day-select="' + handle + '"]' );
				const selectedOption = select.options[select.selectedIndex];

				if (daySelect instanceof HTMLSelectElement && selectedOption instanceof HTMLOptionElement) {

					const dayValue    = parseInt( daySelect.value, 10 );
					const daysInMonth = parseInt( selectedOption.getAttribute( 'data-days-in-month' ) || '', 10 );
					let optionHtml    = '';

					for (let day = 1; day <= daysInMonth; day++) {
						optionHtml += `
							<option value="${day}">
								${day}
							</option>
						`;
					}

					daySelect.innerHTML = optionHtml;

					if (dayValue > 0 && dayValue <= daysInMonth) {
						daySelect.value = dayValue.toString();
					}
				}
			}
		} );
	};

	subscriberInfo.style.display = 'block';
	form.style.display           = 'block';

	// show subscriber info

	subscriberInfo.innerHTML = `
		<dl>
			<div>
				<dt>Subscriber:</dt>
				<dd>${subscriber.firstName} ${subscriber.lastName}</dd>
			</div>
			<div>
				<dt>Email:</dt>
				<dd>${subscriber.emailAddress}</dd>
			</div>
		</dl>
	`;


	// populate date dropdowns

	monthSelects.forEach( select =>
	{
		for (let month = 1; month <= 12; month++) {

			const date        = new Date( 2021, month, 0 ); // non-leap year chosen to avoid feb. 29th issues
			const monthName   = date.toLocaleString( 'default', { month: 'long' } );
			const daysInMonth = date.getDate();

			select.innerHTML += `
				<option
					value="${month}"
					data-days-in-month="${daysInMonth}"
				>
					${monthName}
				</option>
			`;
		}

		select.addEventListener( 'change', () => updateDaysInMonths() );
	} );

	updateDaysInMonths();

	// set preferences

	const settings   = subscriber.settings;
	const startMonth = settings.adviceNotificationDateStart.month;
	const startDay   = settings.adviceNotificationDateStart.day;
	const endMonth   = settings.adviceNotificationDateEnd.month;
	const endDay     = settings.adviceNotificationDateEnd.day;

	subscriberToken.value = token;

	if (settings.restrictAdviceNotificationsByDateRange) {

		notificationType.value = 'restricted-by-date-range';

	} else {

		notificationType.value = 'year-round';
	}


	if (startMonth >= 1 && startMonth <= 12) {
		startMonthElem.value = startMonth.toString();
	}

	if (startDay >= 1 && startDay <= 31) {
		startDayElem.value = startDay.toString();
	}

	if (endMonth >= 1 && endMonth <= 12) {
		endMonthElem.value = endMonth.toString();
	} else {
		endMonthElem.value = '12';
	}

	if (endDay >= 1 && endDay <= 31) {
		endDayElem.value = endDay.toString();
	} else {
		endDayElem.value = '31';
	}

	// notification type toggle

	toggleNotificationType();

	notificationType.addEventListener( 'change', () => toggleNotificationType() );

	// form submit

	handleFormSubmit( form );
};


/**
 *
 */
const handleFormSubmit = ( form: HTMLFormElement ) =>
{
	form.addEventListener( 'submit', e =>
	{
		e.preventDefault();

		const wateringAdviceApiUrl = getAppConfigVar( 'wateringAdviceApiUrl' );
		const url                  = wateringAdviceApiUrl + '/api/subscribers/preferences/save/';
		const submitButton         = document.querySelector( '#subscriber-preferences-save' );
		const formControls         = form.querySelectorAll( 'select, input' );
		const formData             = new FormData( form );
		const options: RequestInit = {
			method: 'post',
			mode:   'cors',
			cache:  'no-cache',
			body:   formData,
		};

		if (!(submitButton instanceof HTMLElement)) {
			return;
		}


		hideErrorMessages();
		hideSuccessMessages();
		submitButton.setAttribute( 'disabled', 'disabled' );
		formControls.forEach( formControl => formControl.classList.remove( 'error' ) );


		fetch( url, options )
			.then( response => response.json() )
			.then( ( response: ApiSubscriberPreferencesSaveResponse ) =>
			{
				submitButton.removeAttribute( 'disabled' );

				if (response.success) {

					showSuccessMessages( ['Your settings were saved.'] );

				} else {

					const errorMessages: string[] = [];

					if (response.fieldErrors) {

						response.fieldErrors.forEach( ( err: ApiSubscriberPreferencesSaveFieldError ) =>
						{
							const elem = document.querySelector( `[name="${err.field}"]` );

							if (elem instanceof HTMLElement) {
								errorMessages.push( err.msg );
								elem.classList.add( 'error' );
							}
						} );
					}

					if (errorMessages.length === 0 && response.errorMsg && response.errorMsg.length > 0) {
						errorMessages.push( response.errorMsg );

					}

					if (errorMessages.length === 0) {
						errorMessages.push( 'An unknown error occurred.' );
					}

					showErrorMessages( errorMessages );
				}
			} );
	} );
};


/**
 *
 * @param messages
 */
const showErrorMessages = ( messages: string[] ) =>
{
	const errorMessageElem = document.querySelector( '#error-messages' );

	if (errorMessageElem instanceof HTMLElement) {

		errorMessageElem.classList.add( 'visible' );
		errorMessageElem.innerHTML = `<ul><li>${messages.join( '</li><li>' )}</li></ul>`;

		errorMessageElem.scrollIntoView();
	}
};


/**
 *
 */
const hideErrorMessages = () =>
{
	const errorMessageElem = document.querySelector( '#error-messages' );

	if (errorMessageElem instanceof HTMLElement) {

		errorMessageElem.classList.remove( 'visible' );
		errorMessageElem.innerHTML = ``;
	}
};


/**
 *
 */
const showSuccessMessages = ( messages: string[] ) =>
{
	const successMessageElem = document.querySelector( '#success-messages' );

	if (successMessageElem instanceof HTMLElement) {

		successMessageElem.classList.add( 'visible' );
		successMessageElem.innerHTML = `<ul><li>${messages.join( '</li><li>' )}</li></ul>`;

		successMessageElem.scrollIntoView();
	}
};


/**
 *
 */
const hideSuccessMessages = () =>
{
	const successMessageElem = document.querySelector( '#error-messages' );

	if (successMessageElem instanceof HTMLElement) {

		successMessageElem.classList.remove( 'visible' );
		successMessageElem.innerHTML = ``;
	}
};


/**
 *
 */
export default setupSubscriberPreferencesForm;